import { InjectionMoldingOrderedProductStatus } from '../enums/injection-molding-ordered-product-status.enum';
import { ProductVersionStatus } from '../enums/product-version-status.enum';
import { Customer } from './customer.model';
import { InjectionMoldingOrder } from './injection-molding-order.model';
import { InjectionMoldingProductVersion } from './injection-molding-product-version.model';
import { UserComment } from './user-comment.model';

/** A generic traceability value. Values stay strings so supplier formatting and leading zeroes survive. */
export interface InjectionMoldingLotTraceabilityEntry {
    key: string;
    value: string;
}

/**
 * An immutable snapshot of the component lots that apply after a production boundary.
 * `effectiveAfterQuantity: 900` means this set begins with finished unit 901.
 */
export interface InjectionMoldingLotTraceabilitySet {
    id: string;
    effectiveAfterQuantity: number;
    lotTraceability: InjectionMoldingLotTraceabilityEntry[];
    createdById: string;
    createdOn: Date;
}

export interface InjectionMoldingLotTraceabilityRange {
    lotSet: InjectionMoldingLotTraceabilitySet;
    fromQuantity: number;
    throughQuantity: number | null;
}

/**
 * One ordered-product line represents one product run. Product details are always read from that
 * product's current released version. `lotTraceability` is the active set used for future labels;
 * `lotTraceabilityHistory` preserves the quantity boundary for each set used during the run.
 */
export interface InjectionMoldingOrderedProduct {
    id: string;
    injectionMoldingOrderId: string;
    productId: string;
    quantity: number;
    completedQuantity: number;
    dueDate: Date;
    salesOrderNumber: string;
    status: InjectionMoldingOrderedProductStatus;
    note: string;
    lotTraceability: InjectionMoldingLotTraceabilityEntry[];
    lotTraceabilityHistory?: InjectionMoldingLotTraceabilitySet[];
    comments: UserComment[];
    completedById: string;
    completedOn: Date | null;

    createdById: string;
    createdOn: Date;
    updatedById: string;
    updatedOn: Date;
    changeLog: InjectionMoldingOrderedProductChangeLog[];
}

/**
 * True when the active set begins after the current completed quantity. Saving another set at the
 * same boundary corrects that upcoming set; it does not represent a lot change after more parts.
 */
export function isInjectionMoldingLotSetCorrection(
    orderedProduct: Pick<
        InjectionMoldingOrderedProduct,
        'completedQuantity' | 'lotTraceability' | 'lotTraceabilityHistory'
    >,
): boolean {
    if (!(orderedProduct.lotTraceability ?? []).length) return false;
    const completedQuantity = orderedProduct.completedQuantity ?? 0;
    const history = orderedProduct.lotTraceabilityHistory ?? [];
    if (!history.length) return completedQuantity === 0;
    const latestBoundary = Math.max(...history.map((lotSet) => lotSet.effectiveAfterQuantity));
    return latestBoundary === completedQuantity;
}

/**
 * Turns append-only lot-set snapshots into human-readable finished-unit ranges. If more than one
 * correction was recorded at the same boundary, the most recently created set is the effective one.
 */
export function toInjectionMoldingLotTraceabilityRanges(
    history: InjectionMoldingLotTraceabilitySet[] | null | undefined,
    orderedQuantity: number,
): InjectionMoldingLotTraceabilityRange[] {
    const sorted = [...(history ?? [])].sort((left, right) => (
        left.effectiveAfterQuantity - right.effectiveAfterQuantity
        || left.createdOn.getTime() - right.createdOn.getTime()
        || left.id.localeCompare(right.id)
    ));
    const effectiveByBoundary = new Map<number, InjectionMoldingLotTraceabilitySet>();
    for (const lotSet of sorted) effectiveByBoundary.set(lotSet.effectiveAfterQuantity, lotSet);
    const effectiveSets = [...effectiveByBoundary.values()].sort((left, right) => (
        left.effectiveAfterQuantity - right.effectiveAfterQuantity
    ));

    return effectiveSets.map((lotSet, index) => {
        const fromQuantity = lotSet.effectiveAfterQuantity + 1;
        const nextBoundary = effectiveSets[index + 1]?.effectiveAfterQuantity;
        return {
            lotSet,
            fromQuantity,
            throughQuantity: nextBoundary ?? (orderedQuantity >= fromQuantity ? orderedQuantity : null),
        };
    }).filter((range) => range.throughQuantity === null || range.throughQuantity >= range.fromQuantity);
}

export interface InjectionMoldingOrderedProductChangeLog {
    id: string;
    createdOn: Date;
    createdById: string;
    text: string;
    /** Optional metadata for quantity entries stored in the existing serialized change log. */
    type?: 'completed-quantity-added' | 'completed-quantity-corrected';
    quantityChange?: number;
    completedQuantityBefore?: number;
    completedQuantityAfter?: number;
}

export function injectionMoldingCommentParticipantIds(
    comments: UserComment[] | null | undefined,
    authorUserId: string,
): string[] {
    return [...new Set((comments ?? [])
        .map((comment) => comment.userId)
        .filter((userId) => !!userId && userId !== authorUserId))];
}

export function latestInjectionMoldingLotBoundary(
    orderedProduct: Pick<InjectionMoldingOrderedProduct, 'lotTraceabilityHistory'>,
): number {
    return Math.max(
        0,
        ...(orderedProduct.lotTraceabilityHistory ?? []).map((lotSet) => lotSet.effectiveAfterQuantity),
    );
}

export interface DisplayInjectionMoldingOrderedProduct {
    orderedProduct: InjectionMoldingOrderedProduct;
    orderedProductVersion: InjectionMoldingProductVersion;
    order: InjectionMoldingOrder;
    orderCustomer: Customer | null;
}

export function toDisplayInjectionMoldingOrderedProducts(
    orderedProducts: InjectionMoldingOrderedProduct[],
    productIdToReleasedVersionDict: Record<string, InjectionMoldingProductVersion>,
    orderDict: Record<string, InjectionMoldingOrder>,
    customerDict: Record<string, Customer>,
): DisplayInjectionMoldingOrderedProduct[] {
    return orderedProducts.flatMap((orderedProduct) => {
        const order = orderDict[orderedProduct.injectionMoldingOrderId];
        if (!order) return [];
        const orderedProductVersion = productIdToReleasedVersionDict[orderedProduct.productId] ?? {
            id: '',
            productId: orderedProduct.productId,
            status: ProductVersionStatus.Obsolete,
            versionNumber: null,
            partNumber: `missing product ${orderedProduct.productId}`,
            description: 'A current released version could not be found for this product.',
            imageUrl: '',
            thumbnailImageUrl: '',
            customerId: order.customerId,
            customerPartNumber: '',
            drawingResourceId: '',
            createdById: '',
            createdOn: orderedProduct.createdOn,
            updatedById: '',
            updatedOn: orderedProduct.updatedOn,
            releasedById: null,
            releasedOn: null,
            releaseComment: '',
            defaultBoxQuantity: null,
            floorVisibilityDays: null,
            estimatedUnitsPerDay: null,
        };

        return [{
            orderedProduct,
            orderedProductVersion,
            order,
            orderCustomer: customerDict[order.customerId] ?? null,
        }];
    });
}

/** Returns one deterministic current Released version per stable product ID. */
export function toReleasedInjectionMoldingProductVersionDict(
    productVersions: InjectionMoldingProductVersion[],
): Record<string, InjectionMoldingProductVersion> {
    return productVersions.reduce((dict, version) => {
        if (version.status !== ProductVersionStatus.Released) return dict;

        const current = dict[version.productId];
        const versionIsNewer = !current
            || (version.versionNumber ?? 0) > (current.versionNumber ?? 0)
            || (
                (version.versionNumber ?? 0) === (current.versionNumber ?? 0)
                && (version.releasedOn?.getTime() ?? version.updatedOn.getTime())
                    > (current.releasedOn?.getTime() ?? current.updatedOn.getTime())
            )
            || (
                (version.versionNumber ?? 0) === (current.versionNumber ?? 0)
                && (version.releasedOn?.getTime() ?? version.updatedOn.getTime())
                    === (current.releasedOn?.getTime() ?? current.updatedOn.getTime())
                && version.id.localeCompare(current.id) > 0
            );
        if (versionIsNewer) {
            dict[version.productId] = version;
        }
        return dict;
    }, {} as Record<string, InjectionMoldingProductVersion>);
}
